import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import { useActiveFeature } from 'services/permissions/permissionChecks';
import { convertDecimalToPrice } from 'services/utils/convertDecimalToPrice';
import '../../style/scss/card.scss';

// Type of the compact periodic invoice data on the contract
interface ContractInvoiceScheduleType {
    id: number;
    template: number;
    interval_type: string;
    is_active: boolean;
    next_invoice_date: string | null;
    amount: string;
    amount_excl_vat: string;
    currency: string | null;
    monthly_revenue: string;
}

// Shows the revenue of the periodic invoice of a contract
const ContractRevenueCard: React.FC<{
    data?: any;
    loadStatus?: 'idle' | 'loading' | 'loaded';
}> = ({
    data,
    loadStatus,
}) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();
    const periodicInvoicesActive = useActiveFeature('periodic_invoices');

    // Get the periodic invoice of the contract
    const schedule: ContractInvoiceScheduleType | null = data?.invoice_schedule ?? null;

    // Without the periodic invoices feature or a periodic invoice there is no revenue to show
    if (!periodicInvoicesActive || loadStatus !== 'loaded' || !schedule) return null;

    // Calculate the yearly revenue from the monthly revenue
    const yearlyRevenue = (Number(schedule.monthly_revenue) * 12).toFixed(2);

    // Format a date value to the user locale
    const formatDate = (value: string | null) => {
        if (!value) return '-';
        return new Date(value).toLocaleDateString(userLocale, { year: 'numeric', month: '2-digit', day: '2-digit' });
    };

    return (
        <div className='card contract-revenue-card'>
            <h6>{t('contract.revenue.card_label')}</h6>
            <div className='contract-revenue-amounts'>
                <div className='contract-revenue-amount'>
                    <div className='contract-revenue-value'>
                        {convertDecimalToPrice(schedule.monthly_revenue, schedule.currency ?? undefined, userLocale)}
                    </div>
                    <div className='contract-revenue-label'>{t('contract.revenue.mrr_label')}</div>
                </div>
                <div className='contract-revenue-amount'>
                    <div className='contract-revenue-value'>
                        {convertDecimalToPrice(yearlyRevenue, schedule.currency ?? undefined, userLocale)}
                    </div>
                    <div className='contract-revenue-label'>{t('contract.revenue.yearly_label')}</div>
                </div>
            </div>
            <div className='contract-revenue-subline'>
                {t(`contract.interval.${schedule.interval_type}`)}
                {schedule.is_active
                    ? ` · ${t('contract.revenue.next_invoice_label', { date: formatDate(schedule.next_invoice_date) })}`
                    : ` · ${t('invoice.schedule.status_stopped').toLowerCase()}`}
            </div>
        </div>
    );
};

export default ContractRevenueCard;
